import React, { useEffect, useMemo } from 'react';
import SectionForm from '../../components/general/SectionForm';
import ClinicalSummaryBanner from '../../components/general/ClinicalSummary/ClinicalSummaryBanner';
import UpdateAllergies from '../../components/general/ClinicalSummary/UpdateAllergies';
import { AllergyType } from '../../components/general/SmartFields/index';
import { IDepartment } from '../../constants/types';
import { useDispatch, useSelector } from 'react-redux';
import { postSubmissionPatientAction, updateSubmissionPatientAction, deleteSubmissionPatientAction, stopTreatmentPatientAction } from '../../redux/actions/submissionsPatientActions';
import Loader from '../../components/Loader';
import { usePrevious } from '../../hooks';
import { MatrixForm } from './patient/Forms/Matrix';
import { cloneDeep } from 'lodash';
import { isSmartField } from '../../utils';
import { TYPE_FILL_SURVEY } from '../../constants';
import { getArchivedFields } from './FillSurvey.archivedFields';
import { buildTreatmentSaveValue, prepareTreatmentSurveyRecordForPersistence } from './FillSurvey.treatments';
import { TextField } from '@mui/material';
import { Translate } from 'react-localize-redux';
import Modal from '../../components/general/modal';
import CryptoJS from 'crypto-js';
import { isWithinSubmissionGracePeriod } from '../../utils/submissionGracePeriod';

interface FillSurveyProps {
    idSubmission?:number,
    sectionSelected?:number,
    sections:any[],
    uuid: string,
    initData?:any,
    country:string,
    uuidInvestigation:string,
    uuidPatient:string,
    requestServiceId?:string,
    nameSurvey?:string,
    // Numeric survey-type enum (see src/constants TYPE_* values); fed in as
    // dataCollection.type. Kept loose (string|number) for legacy call sites.
    typeSurvey?:string|number,
    department?:IDepartment,
    hideButtonsSubmit?:boolean,
    alterSubmitButton?: (button:JSX.Element) => JSX.Element,
    callBackDataCollectionSaved?: () => void
    callBackDataCollectionSavedWithData?:(data:any) => void
}

const FillSurvey: React.FC<FillSurveyProps> = ({ sectionSelected, uuid, sections, initData, 
                                                idSubmission, requestServiceId, nameSurvey,
                                                country, uuidInvestigation, uuidPatient, department, typeSurvey, hideButtonsSubmit,
                                                alterSubmitButton, callBackDataCollectionSaved, callBackDataCollectionSavedWithData }) => {
    const [loading, setLoading] = React.useState(false);
    const [showEditReasonModal, setShowEditReasonModal] = React.useState(false);
    const [editReason, setEditReason] = React.useState('');
    const [editPassword, setEditPassword] = React.useState('');
    const [editPasswordError, setEditPasswordError] = React.useState('');
    const [editReasonError, setEditReasonError] = React.useState('');
    const [pendingEditData, setPendingEditData] = React.useState<{postObj: any, buttonSubmitted: string} | null>(null);
    // SHER-287: "Update allergies" surface state. Opened from the Clinical Summary
    // banner, pre-filled with the patient's current allergy state.
    const [allergyUpdate, setAllergyUpdate] = React.useState<{ current: AllergyType[]; noKnown: boolean } | null>(null);
    const emptyArray:any[] = [];                                                 
    const submissionsSurvey = useSelector((state:any) => {
        console.log(state);
        if(state.patientsSubmissions.data && state.patientsSubmissions.data[uuidPatient] && state.patientsSubmissions.data[uuidPatient][uuid]){
            return state.patientsSubmissions.data[uuidPatient][uuid].submissions
        }
        return emptyArray;
    });
    const prevSubmissionsSurvey = usePrevious(submissionsSurvey, true);
    const dispatch = useDispatch();

    const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;

    function getSubmissionAge(): number {
        if (!initData?.createdAt) return 0;
        return Date.now() - new Date(initData.createdAt).getTime();
    }

    function isSubmissionWithinGracePeriod(): boolean {
        return isWithinSubmissionGracePeriod(initData?.createdAt);
    }

    function isSubmissionOlderThan48h(): boolean {
        return getSubmissionAge() > FORTY_EIGHT_HOURS;
    }

    async function confirmEditWithReason() {
        if (!editReason.trim()) {
            setEditReasonError('required');
            return;
        }
        setEditReasonError('');

        const requiresPassword = isSubmissionOlderThan48h();
        if (requiresPassword && !editPassword) {
            setEditPasswordError('required');
            return;
        }
        setEditPasswordError('');

        if (pendingEditData) {
            const { postObj, buttonSubmitted } = pendingEditData;
            postObj.editReason = editReason.trim();
            if (requiresPassword) {
                postObj.password = CryptoJS.SHA256(editPassword).toString(CryptoJS.enc.Base64);
            }
            setShowEditReasonModal(false);
            setEditReason('');
            setEditPassword('');
            setEditPasswordError('');
            setEditReasonError('');
            setPendingEditData(null);
            await executeUpdate(postObj, buttonSubmitted);
        }
    }

    function closeEditReasonModal() {
        setShowEditReasonModal(false);
        setEditReason('');
        setEditPassword('');
        setEditPasswordError('');
        setEditReasonError('');
        setPendingEditData(null);
        setLoading(false);
    }

    async function executeUpdate(postObj: any, buttonSubmitted: string) {
        try {
            const submissionId = postObj.id || idSubmission;
            await dispatch(updateSubmissionPatientAction(postObj, uuidInvestigation, uuidPatient, uuid, nameSurvey, submissionId));
            if (callBackDataCollectionSaved) {
                callBackDataCollectionSaved();
            }
        } catch (error: any) {
            const errorCode = error.errorCode;
            if (errorCode === 5 || error.errorMessage === 'Edit reason is required') {
                // Grace period expired or client/API clock skew — show edit reason modal
                setPendingEditData({ postObj, buttonSubmitted });
                setShowEditReasonModal(true);
                return;
            }
            if (errorCode === 'PASSWORD_REQUIRED' || errorCode === 'INVALID_PASSWORD') {
                // Reopen modal with password error
                setPendingEditData({ postObj, buttonSubmitted });
                setEditReason(postObj.editReason || '');
                setShowEditReasonModal(true);
                setEditPasswordError(errorCode === 'INVALID_PASSWORD' ? 'incorrect' : 'required');
                return;
            }
            const errorMessage = error.message || error.errorMessage || 'Failed to save submission';
            console.error('Error saving submission:', errorMessage, error);
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    }

    async function promptEditReason(postObj: any, buttonSubmitted: string) {
        // Skip modal during grace period (within 1 hour of creation)
        if (isSubmissionWithinGracePeriod()) {
            await executeUpdate(postObj, buttonSubmitted);
            return;
        }
        setPendingEditData({ postObj, buttonSubmitted });
        setShowEditReasonModal(true);
    }

    let fields:any[] = [];
    let sectionsSurvey = []
    if(sectionSelected){
        sectionsSurvey = [sectionSelected];
    }
    else{
        sectionsSurvey = sections;
    }

    useEffect(() => {
        if(callBackDataCollectionSavedWithData){
            const oldSubmissions = !prevSubmissionsSurvey ? [] : prevSubmissionsSurvey;
            if(submissionsSurvey.length !== oldSubmissions.length){
                const lastSubmission = submissionsSurvey[submissionsSurvey.length - 1];
                callBackDataCollectionSavedWithData(lastSubmission);
            }
            else if(idSubmission ){
                const oldSubmission = oldSubmissions.find((sub:any) => sub.id === idSubmission);
                const submission = submissionsSurvey.find((sub:any) => sub.id === idSubmission);
                if(submission && (oldSubmission && oldSubmission.updatedAt !== submission.updatedAt)){
                    callBackDataCollectionSavedWithData(submission);
                }
            }
        }
        
    }, [submissionsSurvey]);

    useEffect(() => {
        console.log("FillSurvey");
    }, []);

    async function saveTreatmentsWrapper(treatments: any[], forceNewSubmissionAddButton = false, surveyField?: any) {

        console.log("Saving treatments to database:", treatments);
        if (treatments.length === 0) {
            console.error("Treatments array is empty");
            return;
        }

        if (!surveyField) {
            console.error("Treatment field info not provided");
            return;
        }

        const treatmentPayload = buildTreatmentSaveValue({
            surveyField,
            treatments,
            initData,
            forceNewSubmissionAddButton,
        });
        
        // Format data in the same structure that saveRecords expects
        const dataFields = [{
            surveyField: {...surveyField},
            value: treatmentPayload
        }];

        await saveRecords(dataFields, "button1", forceNewSubmissionAddButton);
    }

    // SHER-287: the (plaintext) allergy smart field in this survey, if any. Memoised
    // over `sections` so the multiple call sites (render + save) don't re-scan.
    const allergyField = useMemo<any | null>(() => {
        for (const section of sections) {
            const f = (section.fields ?? []).find((field: any) => field.type === "allergy");
            if (f) return f;
        }
        return null;
    }, [sections]);

    // SHER-369: the allergy Clinical Summary banner is a clinical-encounter aid
    // (SHER-263/287). On technician-typed lab/imaging request fills (RequestSingle ->
    // FillDataCollection -> FillSurvey for TYPE_FILL_LAB_SURVEY / TYPE_FILL_IMG_SURVEY)
    // it is noise, so suppress it there — mirroring how the API's outpatientsAutoComplete
    // excludes those same fill types as "not a clinical encounter". Exception: if such a
    // survey actually carries an allergy field, the banner is also the SHER-287 editing
    // entry point, so the edit path wins and we keep it.
    const isLabOrImagingFill = TYPE_FILL_SURVEY.includes(Number(typeSurvey));
    const showClinicalSummaryBanner = !isLabOrImagingFill || !!allergyField;

    // SHER-287: open the pre-filled "Update allergies" surface. `current` is the
    // Clinical Summary state handed up by the banner (the pre-fill baseline).
    function openAllergyUpdate(current: { values: string[]; noKnown: boolean }) {
        if (!allergyField) return; // survey has no allergy field — nothing to edit
        setAllergyUpdate({
            current: current.values.map((v) => ({ allergy: v, "compo-code": "" })),
            noKnown: current.noKnown,
        });
    }

    // SHER-287: persist the edited FULL allergy list as ONE snapshot submission.
    // This is ALWAYS a brand-new submission (never an edit of whatever submission
    // FillSurvey was opened on), so it becomes the latest allergy snapshot the API
    // projects into the Clinical Summary while the historical submissions are
    // retained. Routed through saveRecords with forceNewSubmissionAddButton=true so
    // it reuses the shared submission-dispatch + loading/error/callback envelope and
    // skips the edit-reason flow that would otherwise target the open submission.
    async function saveAllergySnapshot(snapshot: AllergyType[]) {
        setAllergyUpdate(null);
        if (!allergyField) return;
        await saveRecords([{ surveyField: { ...allergyField }, value: snapshot }], "button1", true);
    }

    async function saveRecords(data:any, buttonSubmitted:string, forceNewSubmissionAddButton = false){
        setLoading(true);
        try {
            let postObj:{
                uuidSurvey: string;
                uuid_patient: string;
                id: number | undefined;
                surveyRecords: any[];
                requestInfo?: {
                    requestId: number;
                    completeRequest: boolean;
                };
            } =  {
                uuidSurvey:uuid, 
                uuid_patient:uuidPatient,
                id:forceNewSubmissionAddButton ? undefined : idSubmission,
                surveyRecords:[]
            }
            if(requestServiceId){
                postObj = {
                    uuidSurvey:uuid, 
                    uuid_patient:uuidPatient,
                    id:forceNewSubmissionAddButton ? undefined : idSubmission,
                    surveyRecords:[],
                    requestInfo: {
                        requestId:parseInt(requestServiceId), 
                        completeRequest:buttonSubmitted === "button2"
                    },
                }
            }
            
            data.forEach((fieldData:any) => {
                postObj.surveyRecords.push(fieldData);
            });

            if(postObj.surveyRecords.length === 0){
                alert("File upload failed. Please retry or remove the file before saving.");
                setLoading(false);
                return;
            }

            //If it is called from onUpdateDatabase with a stopped treatment
            const treatmentSubmission = !forceNewSubmissionAddButton
                ? prepareTreatmentSurveyRecordForPersistence(postObj.surveyRecords[0])
                : null;

            if (treatmentSubmission) {
                const { submissionId, treatmentData, isStoppingTreatment, cleanedTreatments } = treatmentSubmission;
                postObj.surveyRecords[0].value = cleanedTreatments;
                if (isStoppingTreatment) {
                    // This is a STOP action
                    postObj.id = submissionId;
                    idSubmission = postObj.id;

                    const simplifiedPayload = {
                        "treatment-stoppedAt": treatmentData["treatment-stoppedAt"],
                        "treatment-stop_motive": treatmentData["treatment-stop_motive"] || ""
                    };

                    await dispatch(stopTreatmentPatientAction(simplifiedPayload, uuidInvestigation, uuidPatient, uuid, nameSurvey, idSubmission));
                } else {
                    // This is a regular EDIT action — prompt for edit reason
                    postObj.id = submissionId;
                    idSubmission = postObj.id;

                    promptEditReason(postObj, buttonSubmitted);
                    return; // Don't setLoading(false) — modal handles it
                }
            }

            else if(idSubmission && !forceNewSubmissionAddButton){
                // Si hay initData, estamos actualizando — prompt for edit reason.
                // forceNewSubmissionAddButton callers (the "+" add-record path, the
                // allergy snapshot save) always want a brand-new submission even when
                // FillSurvey was opened on an existing one, so they skip the edit flow.
                promptEditReason(postObj, buttonSubmitted);
                return; // Don't setLoading(false) — modal handles it
            }
            else{
                await dispatch(postSubmissionPatientAction(postObj, uuidInvestigation, uuidPatient, uuid, nameSurvey, typeSurvey));
            }
            if(callBackDataCollectionSaved){
                callBackDataCollectionSaved();
            }
        } catch (error: any) {
            // Display error message to user (SHER-497: avoid "[object Object]" when API error is nested)
            const rawMessage = error.message || error.errorMessage || error.response?.data?.errorMessage;
            const errorMessage = typeof rawMessage === 'string'
                ? rawMessage
                : (rawMessage ? JSON.stringify(rawMessage) : 'Failed to save submission');
            console.error('Error saving submission:', errorMessage, error);

            // You can add a snackbar or alert here to show the error to the user
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    }

    async function deleteTreatment(treatment: any){
        setLoading(true);

        const submissionId = treatment.submission_id;
        const { submission_id, ...cleanedTreatment } = treatment;

        await dispatch(deleteSubmissionPatientAction(cleanedTreatment, uuidInvestigation, submissionId, uuidPatient, uuid));

        setLoading(false);
    }
    
    sectionsSurvey.sort((a,b) => a.order - b.order)
        .forEach((section, index) =>{
            // Deep clone the fields to avoid mutating Redux store
            let sectionFields = cloneDeep(section.fields).sort((a:any,b:any) => a.order - b.order)
    
            if((sectionFields.length === 1 || sectionFields[0].label === section.name) && (sectionFields[0].type !== "textarea" && isSmartField(sectionFields[0].type))){
                sectionFields[0].label = `general.default-label.${sectionFields[0].type}`
            }
            sectionFields.unshift({
                type:"title_section",
                label:section.name,
                name:"title_section_"+index,
                required:false, 
                validation: "notEmpty",
                id:index
            });
            fields = fields.concat(sectionFields);

            if(nameSurvey === "BOWEL MOVEMENT CHART"){
                fields.splice(1, 0, {
                    type:"thumbnail",
                    label:"https://i0.wp.com/post.medicalnewstoday.com/wp-content/uploads/sites/3/2021/08/15970-bristol-stool-chart-1296x1394-english-body-952x1024.jpg?w=1155&h=2929",
                    value:"Bristol stool chart",
                    required:false, 
                    validation: "notEmpty",
                    id:1
                });
            }

            if(nameSurvey === "PAIN VAS"){
                fields.splice(1, 0, {
                    type:"thumbnail",
                    label:"https://michigantoday.umich.edu/wp-content/uploads/sites/89/2025/07/Health-Yourself-Pain-Assessment-Tool.png",
                    value:"Pain Assessment Tool",
                    required:false, 
                    validation: "notEmpty",
                    id:1
                });
            }
    })

    // SHER-208: surface records on soft-deleted fields as read-only archived
    // fields so the historical value stays visible and round-trips on save.
    fields = fields.concat(getArchivedFields(sectionsSurvey, initData));

    if(loading){
        return <Loader />
    }
    if(nameSurvey === "MATRIX FORM"){
        return <MatrixForm survey={{ sections: sections, name: nameSurvey }}
        submissions={submissionsSurvey}
        isVisualizingMode={false}
        callbackSurveyCompleted={ (values:any) => {saveRecords(values, "button2")}}/>
    
    }
    const requiresPassword = isSubmissionOlderThan48h();

    return(
        <>
            {/* SHER-263: surface the patient's Clinical Summary (allergies) before the form.
                SHER-287: when this survey has an allergy field, the banner becomes an
                editing entry point that opens the pre-filled "Update allergies" surface. */}
            {showClinicalSummaryBanner &&
                <ClinicalSummaryBanner
                    uuidInvestigation={uuidInvestigation}
                    uuidPatient={uuidPatient}
                    onUpdate={allergyField ? openAllergyUpdate : undefined}
                />
            }
            {allergyUpdate &&
                <Modal
                    open={true}
                    closeModal={() => setAllergyUpdate(null)}
                    title={<Translate id="clinicalSummary.allergy.update.title" />}
                >
                    <UpdateAllergies
                        country={country}
                        currentAllergies={allergyUpdate.current}
                        noKnown={allergyUpdate.noKnown}
                        onSave={saveAllergySnapshot}
                        onCancel={() => setAllergyUpdate(null)}
                    />
                </Modal>
            }
            <SectionForm key={uuid} initData={initData}
                country={country} uuidInvestigation={uuidInvestigation}
                fields={fields} uuidPatient={uuidPatient} department={department}
                uuidSurvey = {uuid} alterSubmitButton={alterSubmitButton}
                idSubmission={idSubmission}
                hideButtonsSubmit={hideButtonsSubmit}
                onSaveToDatabase={(treatment: any, surveyField: any) => saveTreatmentsWrapper([treatment], true, surveyField)}
                onUpdateDatabase={(treatment: any, surveyField: any) => saveTreatmentsWrapper([treatment], false, surveyField)}
                onDeleteDatabase={(treatment: any) => deleteTreatment(treatment)}
                callBackSectionForm = {(values:any, buttonSubmitted:any) => saveRecords(values, buttonSubmitted)}/>
            <Modal
                open={showEditReasonModal}
                closeModal={closeEditReasonModal}
                title={<Translate id="hospital.submission-edit-reason-title" />}
                confirmAction={confirmEditWithReason}
                confirmButtonLabel="hospital.submission-edit-confirm"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', minWidth: '300px' }}>
                    <TextField
                        label={<Translate id="hospital.submission-edit-reason-label" />}
                        placeholder=""
                        value={editReason}
                        onChange={(e) => { setEditReason(e.target.value); setEditReasonError(''); }}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        error={!!editReasonError}
                        helperText={editReasonError ? <Translate id="hospital.submission-edit-reason-required" /> : ''}
                        autoFocus
                    />
                    {requiresPassword && (
                        <TextField
                            label={<Translate id="hospital.submission-edit-password-label" />}
                            type="password"
                            value={editPassword}
                            onChange={(e) => { setEditPassword(e.target.value); setEditPasswordError(''); }}
                            fullWidth
                            required
                            error={!!editPasswordError}
                            helperText={editPasswordError === 'incorrect'
                                ? <Translate id="hospital.submission-edit-password-incorrect" />
                                : <Translate id="hospital.submission-edit-password-required" />}
                        />
                    )}
                </div>
            </Modal>
        </>
    )
   
};

export default FillSurvey;
